import { createBrowserHistory } from "history";
import { createRoot } from "react-dom/client";
import { Router } from "react-router-dom";
import { LEAVE_BLOCKER_KEY, leaveBlockerCallback } from "../components/LeaveBlocker/LeaveBlocker";
import { ApiProvider } from "../providers/ApiProvider";
import { AppStoreProvider } from "../providers/AppStoreProvider";
import { ConfigProvider } from "../providers/ConfigProvider";
import { MultiProvider } from "../providers/MultiProvider";
import { ProjectProvider } from "../providers/ProjectProvider";
import { RoutesProvider } from "../providers/RoutesProvider";
import { RouteGuard } from "../routing/RouteGuard";
import { BrandingProvider } from "../providers/BrandingProvider";
import { DRAFT_GUARD_KEY, DraftGuard, draftGuardCallback } from "../components/DraftGuard/DraftGuard";
import { InstallPrompt } from "../components/InstallPrompt/InstallPrompt";
import { AsyncPage } from "./AsyncPage/AsyncPage";
import ErrorBoundary from "./ErrorBoundary";
import { FF_UNSAVED_CHANGES, isFF } from "../utils/feature-flags";
import { ToastProvider, ToastViewport } from "@humansignal/ui";
import { JotaiProvider, JotaiStore } from "../utils/jotai-store";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@humansignal/core/lib/utils/query-client";
import { RootPage } from "./RootPage";
import { installOfflineFirstQueries } from "../utils/offline-first-queries";
installOfflineFirstQueries(queryClient);
import "@humansignal/ui/src/tailwind.css";
import "./App.prefix.css";
import { AuthProvider } from "@humansignal/core/providers/AuthProvider";
import { LocaleProvider } from "../i18n/LocaleProvider";
import { LangSyncFromUser } from "../i18n/LangSyncFromUser";
import { AppSettingsUserSync } from "./AppSettingsUserSync";
import { ThemeSync } from "../components/ThemeSync/ThemeSync";
import { activeHostMode, wrapHistoryWithMapping } from "../utils/hostMode";

const baseURL = new URL(APP_SETTINGS.hostname || location.origin);
export const UNBLOCK_HISTORY_MESSAGE = "UNBLOCK_HISTORY";

const browserHistory = createBrowserHistory({
  basename: baseURL.pathname || "/",
  // callback is an async way to confirm or decline going to another page in the context of routing. It accepts `true` or `false`
  getUserConfirmation: (message, callback) => {
    // `history.block` doesn't block events, so in the case of listeners,
    // we need to have some flag that can be checked for preventing related actions
    // `isBlocking` flag is used for this purpose
    browserHistory.isBlocking = true;
    const callbackWrapper = (result) => {
      browserHistory.isBlocking = false;
      callback(result);
      isFF(FF_UNSAVED_CHANGES) && window.postMessage({ source: "label-studio", payload: UNBLOCK_HISTORY_MESSAGE });
    };
    if (message === DRAFT_GUARD_KEY) {
      draftGuardCallback.current = callbackWrapper;
    } else if (isFF(FF_UNSAVED_CHANGES) && message === LEAVE_BLOCKER_KEY) {
      leaveBlockerCallback.current = callbackWrapper;
    } else {
      // Intentional ``window.confirm`` — this runs inside react-router's
      // ``getUserConfirmation`` which expects a synchronous boolean decision
      // to decide whether a route transition should proceed. Replacing with
      // our async askConfirm would let the navigation fire before the
      // operator even sees the prompt. Every other confirm / prompt in the
      // app is routed through ``utils/ask.jsx``.
      callbackWrapper(window.confirm(message));
    }
  },
});

// An extension host mode may show clean URLs while the router keeps its
// registered paths (see utils/hostMode.js).
const routerHistory = activeHostMode?.pathMapping
  ? wrapHistoryWithMapping(browserHistory, activeHostMode.pathMapping)
  : browserHistory;

window.LSH = routerHistory;
// Exposed for utils/navigation.spaNavigate — navigating through the
// router's own history re-renders reliably; synthetic popstate did not.
window.__afriRouterHistory = routerHistory;

const App = ({ content }) => {
  return (
    <ErrorBoundary>
      <Router history={routerHistory}>
        {/* LocaleProvider sits at the root of the provider tree so
            every screen — even the unauthenticated login flow — can
            reach into ``useTranslation()``. ``initialLang`` is null
            here; it's overridden at runtime by the user's profile
            preference (and persisted in localStorage as a fallback
            for the unauthenticated state). */}
        <LocaleProvider initialLang={null}>
          <MultiProvider
            providers={[
              <QueryClientProvider client={queryClient} key="query" />,
              <JotaiProvider key="jotai" store={JotaiStore} />,
              <AuthProvider key="auth" />,
              <AppStoreProvider key="app-store" />,
              <ToastProvider key="toast" />,
              <ApiProvider key="api" />,
              <BrandingProvider key="branding" />,
              <ConfigProvider key="config" />,
              <RoutesProvider key="rotes" />,
              <ProjectProvider key="project" />,
            ].filter(Boolean)}
          >
            {/* Always-mounted theme controller — applies the user's
              saved theme (or system theme in Auto mode) to <html>
              data-color-scheme on every mount, watches for OS
              theme changes, and reacts to atom updates from other
              tabs. The ThemeSegment inside the user dropdown used
              to be the only place that wrote the attribute post-
              boot-script, which meant the theme appeared to "only
              apply when the dropdown opens." Renders nothing. */}
            <ThemeSync />
            {/* Adopts the user's saved ``preferred_language`` once
              auth finishes loading, but only on devices where the
              user hasn't already picked a language locally. */}
            <LangSyncFromUser />
            {/* Mirrors ``useAuth().user`` into
              ``window.APP_SETTINGS.user`` so LSF (which falls back
              to APP_SETTINGS.user when its own snapshot's user
              isn't a number) sees the real user. Capacitor only —
              web + desktop already have it populated by Django's
              base.html template. Renders nothing. */}
            <AppSettingsUserSync />
            <AsyncPage>
              <DraftGuard />
              {/* Single source of truth for route access control.
                Declarative policies in src/routing/routePolicy.ts
                decide who can stand on each URL; individual page
                components no longer run their own "if not authorized
                → redirect" useEffect logic. */}
              <RouteGuard>
                <RootPage content={content} />
              </RouteGuard>
              <ToastViewport />
              {/* PWA install pill — renders bottom-right when the
                browser's ``beforeinstallprompt`` fires. No-op on
                Safari/Firefox and when already installed. */}
              <InstallPrompt />
            </AsyncPage>
          </MultiProvider>
        </LocaleProvider>
      </Router>
    </ErrorBoundary>
  );
};

const root = document.querySelector(".app-wrapper");
const content = document.querySelector("#main-content");

// Boot-time error surface — if anything fails before React mounts (esp. on
// Safari which silently blanks on unhandled top-level JS errors), show the
// error text in-page instead of a blank screen. This has saved debugging many
// Safari boots.
const showBootError = (err) => {
  try {
    const msg = err && (err.stack || err.message) ? err.stack || err.message : String(err);
    root.innerHTML = `
      <div style="padding:32px;max-width:720px;margin:40px auto;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1a1a22;">
        <h2 style="margin:0 0 12px;">Something went wrong during app boot</h2>
        <p style="color:#555;margin:0 0 16px;">The JavaScript bundle failed to initialise. Copy the error below and share it — it tells us exactly what's breaking.</p>
        <pre style="background:#f7f7f9;border:1px solid #e5e5ea;border-radius:6px;padding:12px;font-size:12px;line-height:1.5;white-space:pre-wrap;word-break:break-word;">${String(msg).replace(/[<&]/g, (c) => ({ "<": "&lt;", "&": "&amp;" })[c])}</pre>
        <p style="margin:16px 0 0;font-size:12px;color:#888;">Try: hard refresh (Cmd+Shift+R), clear cache (Cmd+Opt+E in Safari), or disable extensions.</p>
      </div>
    `;
  } catch (_) {
    /* last-resort: leave the blank page */
  }
};

// Boot-error surface: ONLY active before React's first commit. Once
// the root has rendered, switch off — otherwise a runtime unhandled
// promise rejection from anywhere in the app (a flaky API call, a
// React-Query mutation throw, a third-party script) wipes the
// entire DOM and replaces it with a "boot failed" overlay. The user
// thinks the app crashed when really it was a single failed save.
let __reactMounted = false;
const __bootHandlers = {
  error: (e) => {
    if (!__reactMounted) showBootError(e.error || e.message);
  },
  rejected: (e) => {
    if (!__reactMounted) showBootError(e.reason);
  },
};
window.addEventListener("error", __bootHandlers.error);
window.addEventListener("unhandledrejection", __bootHandlers.rejected);

// React 18: create the root once and cache it on the container so HMR
// re-renders reuse the same root instead of mounting a fresh tree every
// time (which would leak state and confuse React).
const __ROOT_KEY = "__labelStudioReactRoot";
try {
  const reactRoot = root[__ROOT_KEY] || (root[__ROOT_KEY] = createRoot(root));
  reactRoot.render(<App content={content.innerHTML} />);
  // Flip the gate after the synchronous render call returns.
  // ``createRoot.render()`` schedules the actual paint; the queue
  // microtask fires before React's first commit, so this approximates
  // "React has accepted the tree" closely enough for our purposes —
  // any error from BEFORE this point is a real boot error.
  Promise.resolve().then(() => {
    __reactMounted = true;
  });
} catch (err) {
  showBootError(err);
}

if (module?.hot) {
  module.hot.accept(); // Enable HMR for React components
}
